// Die Aktoren aus dem Homelab-Protokoll (Topic `aktor.stand`; docs/protokoll.md
// › „Aktoren über das Protokoll“) in genau die Form, die die Seiten aus der
// REST-Antwort `GET /v2-api/aktoren/summary` kennen (`AktorDevice`).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/aktoren-protokoll.test.mjs) sie laden kann. Die Stände kommen skaliert an
// (`skaliere("aktor.stand", …)`, nur die Batterie ist skaliert); Gegenstück der
// Rechnung ist internal/protokoll/adapter/aktoren.go.
//
// Regel, mit der die REST-Antwort exakt wiederkehrt (der Adapter sendet
// spiegelbildlich):
//   - `name`, `kind`, `state` und `last_seen` führt die REST-Antwort ohne
//     `omitempty`: fehlt `zustand` im Stand (Fernbedienung), ist `state` "";
//   - `brightness`, `color_temp`, `color_hex`, `available`, `battery` und
//     `linkquality` (omitempty) stehen am Ergebnis nur, wenn der Stand sie trägt
//     -- auch als 0/false -- und sonst gar nicht (nicht als `undefined`);
//   - `last_seen` schreibt Go in UTC auf die Sekunde („…Z“); fehlt die Zeit im
//     Stand (Nullzeit), setzt der Browser genau den Text wieder ein, den Go für
//     sie schreibt ("0001-01-01T00:00:00Z").

import type { AktorDevice } from "./aktoren-summary.svelte";
import type { AktorStand } from "./protokoll/schema.gen";

/** So schreibt Go `time.Time{}` als JSON. */
export const NULLZEIT = "0001-01-01T00:00:00Z";

/** Instanzname → Id des Aktors: „aktor.ab12cd34ef56ab78.stand“ → „ab12cd34ef56ab78“. Alles andere → undefined. */
export function aktorIdAusInstanz(instanz: string): string | undefined {
  const t = instanz.split(".");
  if (t.length !== 3 || t[0] !== "aktor" || t[2] !== "stand" || !/^[0-9a-f]+$/.test(t[1])) return undefined;
  return t[1];
}

/** Unix-Millisekunden → Text wie Go (`time.RFC3339`, UTC, ohne Bruchteile);
 *  fehlt/unlesbar → die Nullzeit der REST-Antwort. */
function zeit(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms)) return NULLZEIT;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return NULLZEIT;
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** Setzt `o[k]`, wenn der Stand einen Wert trägt (auch 0/false). */
function wenn<T extends object, K extends keyof T>(o: T, k: K, v: T[K] | null | undefined): void {
  if (v != null) o[k] = v as T[K];
}

/** Der Aktor aus seinem Stand, wie `GET /v2-api/aktoren/summary` ihn führt. */
export function aktorAusProtokoll(st: AktorStand): AktorDevice {
  const art = st.art === "light" || st.art === "remote" ? st.art : "switch";
  const o: AktorDevice = { name: st.name ?? "", kind: art, state: st.zustand ?? "", last_seen: zeit(st.zuletzt) };
  wenn(o, "brightness", st.helligkeit);
  wenn(o, "color_temp", st.farbtemperatur);
  wenn(o, "color_hex", st.farbe);
  wenn(o, "available", st.erreichbar);
  wenn(o, "battery", st.batterie);
  wenn(o, "linkquality", st.funk);
  return o;
}

/** Die Reihenfolge der REST-Antwort (nach name; Go vergleicht Byte für Byte,
 *  hier Codeeinheit für Codeeinheit -- gleich, solange kein Name Zeichen
 *  außerhalb der Grundebene trägt; nicht `localeCompare`). */
export function vergleicheAktor(a: Pick<AktorDevice, "name">, b: Pick<AktorDevice, "name">): number {
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
}
